import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCcw, Smile, Meh, Frown, MessageSquare } from "lucide-react";
import { apiGet } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const RATING_META = {
  great: { Icon: Smile, label: "Great", tone: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10" },
  ok: { Icon: Meh, label: "OK", tone: "text-[hsl(var(--warning))]", bg: "bg-[hsl(var(--warning))]/10" },
  needs_work: { Icon: Frown, label: "Needs work", tone: "text-destructive", bg: "bg-destructive/10" },
};

const AdminFeedbackPage = () => {
  const [data, setData] = useState({ items: [], stats: null });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const d = await apiGet("/admin/feedback?limit=500");
      setData(d);
    } catch (e) {
      toast.error("Could not load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = data?.stats;

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">User feedback</h1>
          <p className="text-sm text-muted-foreground">
            Read all messages submitted via the in-app feedback button.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-feedback-refresh">
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="admin-feedback-stats">
          <StatCard label="Total feedback" value={stats.total} />
          <StatCard
            label="Great"
            value={`${stats.great} (${stats.great_pct}%)`}
            Icon={Smile}
            tone="text-emerald-600 dark:text-emerald-400"
            bg="bg-emerald-500/10"
          />
          <StatCard
            label="OK"
            value={`${stats.ok} (${stats.ok_pct}%)`}
            Icon={Meh}
            tone="text-[hsl(var(--warning))]"
            bg="bg-[hsl(var(--warning))]/10"
          />
          <StatCard
            label="Needs work"
            value={`${stats.needs_work} (${stats.needs_work_pct}%)`}
            Icon={Frown}
            tone="text-destructive"
            bg="bg-destructive/10"
          />
        </div>
      )}

      {/* Distribution bar */}
      {stats && stats.total > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Rating distribution</CardTitle>
            <CardDescription>{stats.total} responses total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex h-3 rounded-full overflow-hidden" data-testid="admin-feedback-distribution">
              <div className="bg-emerald-500" style={{ width: `${stats.great_pct}%` }} title={`Great ${stats.great_pct}%`} />
              <div className="bg-amber-500" style={{ width: `${stats.ok_pct}%` }} title={`OK ${stats.ok_pct}%`} />
              <div className="bg-rose-500" style={{ width: `${stats.needs_work_pct}%` }} title={`Needs work ${stats.needs_work_pct}%`} />
            </div>
            <div className="flex justify-between text-[11px] text-muted-foreground mt-2">
              <span>Great {stats.great_pct}%</span>
              <span>OK {stats.ok_pct}%</span>
              <span>Needs work {stats.needs_work_pct}%</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4 text-primary" /> Recent messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data.items || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No feedback received yet.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="admin-feedback-list">
              {data.items.map((f) => {
                const meta = RATING_META[f.rating] || RATING_META.ok;
                const Icon = meta.Icon;
                return (
                  <li
                    key={f.id}
                    className={`flex gap-3 rounded-lg border border-border p-3 ${meta.bg}`}
                    data-testid={`admin-feedback-item-${f.rating}`}
                  >
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${meta.tone}`} />
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 text-xs flex-wrap">
                        <Badge variant="outline" className={`${meta.tone}`}>{meta.label}</Badge>
                        <span className="text-muted-foreground">
                          {f.user_email || "(anonymous)"}
                          {f.user_tier && ` · ${f.user_tier}`}
                        </span>
                        <span className="text-muted-foreground ml-auto">
                          {f.created_at ? new Date(f.created_at).toLocaleString() : "—"}
                        </span>
                      </div>
                      {f.message && (
                        <p className="text-sm whitespace-pre-wrap">{f.message}</p>
                      )}
                      {f.page && (
                        <Link
                          to={f.page}
                          className="text-[11px] text-primary hover:underline"
                          data-testid={`admin-feedback-page-link-${f.id}`}
                        >
                          {f.page}
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({ label, value, Icon, tone, bg }) => (
  <Card className={bg || ""}>
    <CardContent className="p-4 flex items-center gap-3">
      {Icon && <Icon className={`h-6 w-6 ${tone}`} />}
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-lg font-semibold">{value}</div>
      </div>
    </CardContent>
  </Card>
);

export default AdminFeedbackPage;
